/**
 * ガチャ画面(docs/02_game_design.md §7.2 / docs/10_paltown_platform.md §4.3)。
 *
 * - 単発 / 連ガチャ(コイン消費。残高不足のボタンは押せない)
 * - **排出率表示は必ず出す**(景品表示・ガイドライン対応。データから生成する)
 * - 抽選演出: カプセルが揺れて開く CSS アニメーション(Phaser は使わない)
 * - 結果一覧: NEW! / 重複はコイン変換額を表示
 * - **モジュール別ガチャ**: ノーマル(コイン)と教科ガチャ(かず・さんすう…)をタブで選ぶ。
 *   教科ガチャは**まなびで貯めた教科コインでしか回せず、そこでしか出ないアイテム**が入る
 * - **チャレンジ限定ガチャ**(docs/12 §3): トロフィーコインでしか回せない。
 *   「こんげつの げんてい」を回す前に見せ、コインが無い子には
 *   チャレンジへの導線を出す(まなびでは1枚も貯まらない通貨なので案内が違う)
 *
 * 価格・排出率・保証・変換額・支払い通貨はすべてカタログ
 * (`content/v1/catalog/gachas.json`)由来。実際の引き落としはサーバーが決める。
 */

import { useEffect, useMemo, useState } from 'react';
import { SLOT_LABELS } from '../logic/avatar';
import {
  buildPool,
  gachaCost,
  hasLimitedEntries,
  isGachaOpen,
  limitedEntries,
  pullCounts,
  rarityRateRows,
} from '../logic/gacha';
import {
  coinSourceHint,
  currencyLabel,
  TROPHY_CURRENCY,
  TYPING_CURRENCY,
} from '../services/wallet';
import { useGameStore } from '../store/gameStore';

/** カプセルが揺れてから結果を見せるまで(ms)。 */
const CAPSULE_MS = 900;

/** タブに出せるガチャ(カタログの1件ぶん。ここで見るのはIDと通貨だけ)。 */
interface SelectableGacha {
  readonly id: string;
  readonly currency: string;
}

/**
 * いま開くガチャを決める。
 *
 * `target` は**ガチャID か 通貨キー**(`gameStore.gachaTarget`)。
 * 通貨が普遍領域へ統合された(docs/13 §9.1)ことで、かずガチャと
 * さんすうガチャは**同じ `math` 通貨の別ガチャ**になった。
 * そのためタブの選択はIDで行い、通貨は「まなびのリザルトから来たときの
 * ざっくりした指定」として後ろに置く。どちらにも当たらなければ先頭。
 */
export function resolveGacha<T extends SelectableGacha>(
  available: readonly T[],
  target: string,
): T | null {
  return (
    available.find((gacha) => gacha.id === target) ??
    available.find((gacha) => gacha.currency === target) ??
    available[0] ??
    null
  );
}

export function GachaScreen() {
  const state = useGameStore();
  const catalog = state.catalog;
  const balances = state.walletBalances;

  /**
   * 出せるガチャ。ノーマルは常に出す。教科ガチャは**残高が分かるときだけ**出す
   * (ローカルモードでは教科コインが1枚も貯まらないので、タブごと出さない)。
   */
  const available = useMemo(
    () =>
      catalog.gachas.filter(
        (g) => g.currency === TYPING_CURRENCY || balances?.[g.currency] !== undefined,
      ),
    [catalog, balances],
  );
  const gacha = resolveGacha(available, state.gachaTarget);
  /** タイピングのコインは進捗が正、教科コインはサーバーが返した残高。 */
  const coins =
    gacha === null || gacha.currency === TYPING_CURRENCY
      ? state.progress.coins
      : (balances?.[gacha.currency] ?? 0);
  const label = currencyLabel(gacha?.currency ?? TYPING_CURRENCY);

  const [revealed, setRevealed] = useState(false);

  // 抽選中は演出を出し、少し待ってから結果を開く。
  useEffect(() => {
    if (state.gachaStatus !== 'done' || !state.gachaResult) {
      setRevealed(false);
      return;
    }
    setRevealed(false);
    const timer = setTimeout(() => setRevealed(true), CAPSULE_MS);
    return () => clearTimeout(timer);
  }, [state.gachaStatus, state.gachaResult]);

  /** 画面を開いた時刻。月替わり限定(docs/12 §3)の当月判定に使う(表示だけ)。 */
  const now = useMemo(() => new Date(), []);

  const pool = useMemo(
    () => (gacha ? buildPool(gacha, (id) => catalog.itemsById.get(id), now) : []),
    [gacha, catalog, now],
  );

  /**
   * こんげつの げんてい(docs/12 §3)。
   * 「その月しか出ない」ものは、**回す前に**何が入っているか見せる。
   */
  const limited = useMemo(
    () =>
      gacha === null
        ? []
        : limitedEntries(gacha, now)
            .map((entry) => catalog.itemsById.get(entry.itemId))
            .filter((item): item is NonNullable<typeof item> => item !== undefined),
    [gacha, catalog, now],
  );

  if (state.catalogStatus === 'loading' || state.catalogStatus === 'idle') {
    return (
      <div className="screen gacha">
        <p className="loading-text">よみこみちゅう…</p>
      </div>
    );
  }

  if (!gacha) {
    return (
      <div className="screen gacha">
        <p className="error-text">ガチャの データを よみこめませんでした</p>
        <button type="button" className="big-button back" onClick={state.backFromMeta}>
          もどる
        </button>
      </div>
    );
  }

  const open = isGachaOpen(gacha, new Date());
  const rates = rarityRateRows(gacha, pool);
  const busy = state.gachaStatus === 'pulling' || (state.gachaStatus === 'done' && !revealed);
  const result = revealed ? state.gachaResult : null;

  return (
    <div className="screen gacha">
      <header className="stage-select-header">
        <button type="button" className="back-button" onClick={state.backFromMeta}>
          もどる
        </button>
        <h1 className="screen-title">{gacha.name}</h1>
        <p className="coin-line" aria-label={`${label.name} ${coins}`}>
          <span className="coin-icon" aria-hidden="true">
            {label.icon}
          </span>{' '}
          {coins}
        </p>
      </header>

      {/*
       * ガチャえらび(docs/10 §4.3)。教科ガチャは、まなびで貯めた
       * その教科のコインでしか回せない。タブに残高をそのまま出しておく。
       */}
      {available.length > 1 ? (
        <div className="gacha-tabs" role="tablist" aria-label="ガチャえらび">
          {available.map((candidate) => {
            const tab = currencyLabel(candidate.currency);
            const owned =
              candidate.currency === TYPING_CURRENCY
                ? state.progress.coins
                : (balances?.[candidate.currency] ?? 0);
            return (
              <button
                key={candidate.id}
                type="button"
                role="tab"
                className="gacha-tab"
                // 同じ通貨のガチャが複数ある(かず・さんすう)ので、印はIDで付ける。
                data-gacha={candidate.id}
                data-currency={candidate.currency}
                aria-selected={candidate.id === gacha.id}
                disabled={busy}
                onClick={() => state.selectGacha(candidate.id)}
              >
                <span className="gacha-tab-name">{candidate.name}</span>
                <span className="gacha-tab-coins">
                  <span className="coin-icon" aria-hidden="true">
                    {tab.icon}
                  </span>{' '}
                  {owned}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="gacha-body">
        <div className="gacha-machine">
          <div className={`capsule ${busy ? 'shaking' : ''} ${result ? 'opened' : ''}`}>
            <span className="capsule-emoji">{result ? '🎉' : '🥚'}</span>
          </div>

          <div className="gacha-buttons">
            {pullCounts(gacha).map((count) => {
              const cost = gachaCost(gacha, count);
              const affordable = coins >= cost;
              return (
                <button
                  key={count}
                  type="button"
                  className={`big-button pull pull-${count}`}
                  disabled={!affordable || !open || busy}
                  onClick={() => void state.pullGacha(gacha.id, count)}
                >
                  <span className="pull-count">
                    {count === 1 ? '1かい ひく' : `${count}れん ひく`}
                  </span>
                  <span className="pull-cost">
                    <span className="coin-icon" aria-hidden="true">
                      {label.icon}
                    </span>{' '}
                    {cost}
                  </span>
                  {count > 1 ? (
                    <span className="pull-note">
                      {gacha.guarantee.minRarity}いじょう 1つ かくてい
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {!open ? <p className="error-text">いまは ひけない ガチャです</p> : null}
          {state.gachaError ? <p className="error-text">{state.gachaError}</p> : null}
          {/*
           * 足りないときの案内は「どこで貯まるコインか」を出す。
           * 教科ガチャはまなびモードでしか貯まらないので、そう書く(docs/10 §4.3)。
           */}
          {coins < gacha.cost ? (
            <p className="hint-text">{coinSourceHint(gacha.currency)}</p>
          ) : null}
          {gacha.currency !== TYPING_CURRENCY ? (
            <p className="hint-text">ここでしか でない アイテムが あるよ</p>
          ) : null}
          {/* トロフィーが1まいも無い子にも、どこで もらえるかを必ず出す(docs/12 §3)。 */}
          {gacha.currency === TROPHY_CURRENCY ? (
            <button
              type="button"
              className="big-button back go-challenge"
              onClick={() => void state.goPuzzle()}
            >
              チャレンジに いく
            </button>
          ) : null}
        </div>

        {/*
         * こんげつの げんてい(docs/12 §3「その週・月しか排出されない限定」)。
         * 回す前に中身が分かるようにする(景品表示・保護策と同じ考え方)。
         */}
        {hasLimitedEntries(gacha) ? (
          <section className="gacha-limited" aria-label="こんげつの げんてい">
            <h2 className="section-title">こんげつの げんてい</h2>
            {limited.length > 0 ? (
              <>
                <ul className="gacha-limited-items">
                  {limited.map((item) => (
                    <li key={item.id} className={`gacha-limited-item rarity-${item.rarity}`}>
                      <span className="gacha-item-emoji">{item.emoji}</span>
                      <span className="gacha-item-name">{item.name}</span>
                      <span className="gacha-item-meta">
                        {SLOT_LABELS[item.slot]} / {item.rarity}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="rate-note">こんげつだけ でるよ。つぎの つきには かわるよ</p>
              </>
            ) : (
              <p className="rate-note">
                こんげつの げんていは おやすみ。つぎの つきを おたのしみに
              </p>
            )}
          </section>
        ) : null}

        {/* 排出率表示(法令・ガイドライン対応。必ず出す) */}
        <section className="gacha-rates" aria-label="ていきょうわりあい">
          <h2 className="section-title">でる かくりつ</h2>
          <table className="rate-table">
            <thead>
              <tr>
                <th>レア</th>
                <th>かくりつ</th>
                <th>しゅるい</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((row) => (
                <tr key={row.rarity} className={`rarity-${row.rarity}`}>
                  <td>{row.rarity}</td>
                  <td>{formatPercent(row.percent)}%</td>
                  <td>{row.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="rate-note">
            おなじ ものが でたら {label.name}に かわるよ(
            {(['N', 'R', 'SR', 'UR'] as const)
              .filter((rarity) => rates.some((row) => row.rarity === rarity))
              .map((rarity) => `${rarity}=${gacha.duplicateConvertCoins[rarity]}`)
              .join(' / ')}
            )
          </p>
        </section>
      </div>

      {result ? (
        <section className="gacha-result" aria-label="ガチャの けっか">
          <h2 className="section-title">
            でたよ!{' '}
            <span className="coin-icon" aria-hidden="true">
              {currencyLabel(result.currency).icon}
            </span>{' '}
            -{result.spent}
            {result.convertedCoins > 0 ? ` / へんかん +${result.convertedCoins}` : ''}
          </h2>
          <ul className="gacha-items">
            {result.items.map((item, index) => (
              <li
                key={`${item.itemId}-${index}`}
                className={`gacha-item rarity-${item.rarity} ${item.isNew ? 'is-new' : 'dup'}`}
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <span className="gacha-item-emoji">{item.emoji}</span>
                <span className="gacha-item-name">{item.name}</span>
                <span className="gacha-item-meta">
                  {SLOT_LABELS[item.slot]} / {item.rarity}
                </span>
                {item.isNew ? (
                  <span className="badge new">NEW!</span>
                ) : (
                  <span className="badge dup">
                    <span className="coin-icon" aria-hidden="true">
                      {currencyLabel(result.currency).icon}
                    </span>{' '}
                    +{item.convertedCoins}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div className="result-buttons">
            <button type="button" className="big-button" onClick={state.clearGachaResult}>
              もういちど
            </button>
            <button type="button" className="big-button back" onClick={() => void state.goAvatar()}>
              きせかえに いく
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** 4.5% のような端数だけ小数を残す。 */
function formatPercent(percent: number): string {
  return Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
}
